import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import icons from '../../assets/icons';
import Header from '../../components/header';
import CopyButton from '../../components/copyButton';
import {
  documentationArticles,
  documentationCategories,
  documentationNavigationSubgroups,
  filterDocumentationArticles,
  getDocumentationArticle
} from '../../documentation/articles';
import NotFoundScreen from '../404';

import './styles.scss';

const articlePath = slug => `/documentation/${slug}`;
const articleScrollKey = slug => `documentation-scroll:${slug}`;
const sectionUrl = (slug, id) => `${window.location.href.split('#')[0]}#${articlePath(slug)}#${id}`;
const categoryLabel = category =>
  documentationCategories.find(candidate => candidate.id === category)?.label || category;
const figureSrc = src => `${process.env.PUBLIC_URL || ''}/${src}`;
const renderInlineCode = (text, keyPrefix = 'inline') => text.split(/(`[^`]+`)/g).map((part, index) =>
  part.startsWith('`') && part.endsWith('`')
    ? <code key={`${keyPrefix}-${index}`}>{part.slice(1, -1)}</code>
    : part);

const jumpToSection = (event, id) => {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  const heading = document.getElementById(id);
  if (!heading) return false;
  heading.focus({ preventScroll: true });
  heading.scrollIntoView({ block: 'start' });
  return true;
};

const returnToArticleTop = event => {
  const article = event.currentTarget.closest('article');
  if (!article) return;
  if (window.innerWidth <= 992) {
    article.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    article.closest('.documentation-main')?.scrollTo({ top: 0, behavior: 'smooth' });
  }
  article.querySelector('h1')?.focus({ preventScroll: true });
};

const DocumentationNavigation = ({ activeSlug }) => {
  const [isOpen, setIsOpen] = useState(() => typeof window === 'undefined' || window.innerWidth > 992);
  const sidebarRef = useRef(null);

  useLayoutEffect(() => {
    if (!activeSlug || window.innerWidth <= 992) return;
    const sidebar = sidebarRef.current;
    const activeLink = sidebar?.querySelector('a[aria-current="page"]');
    if (!activeLink || !sidebar.clientHeight) return;

    const sidebarBounds = sidebar.getBoundingClientRect();
    const linkBounds = activeLink.getBoundingClientRect();
    if (linkBounds.top >= sidebarBounds.top && linkBounds.bottom <= sidebarBounds.bottom) return;

    const centeredTop = sidebar.scrollTop + linkBounds.top - sidebarBounds.top -
      (sidebar.clientHeight - linkBounds.height) / 2;
    sidebar.scrollTo({ top: Math.max(0, centeredTop), behavior: 'smooth' });
  }, [activeSlug]);

  return (
    <aside className="documentation-sidebar" ref={sidebarRef}>
      <details
        className="documentation-navigation-menu"
        onToggle={event => setIsOpen(event.currentTarget.open)}
        open={isOpen}>
      <summary>Browse documentation</summary>
      <div className="documentation-navigation-content">
        <Link
          aria-current={!activeSlug ? 'page' : undefined}
          className="documentation-overview-link"
          to="/documentation">
          Documentation overview
        </Link>
        <nav aria-label="Documentation articles">
          {documentationCategories.map(category => {
            const categoryArticles = documentationArticles
              .filter(article => article.category === category.id);
            const subgroups = documentationNavigationSubgroups[category.id] || [];
            const subgroupSlugs = new Set(subgroups.flatMap(subgroup => subgroup.slugs));
            const topLevelArticles = categoryArticles
              .filter(article => !subgroupSlugs.has(article.slug));

            return (
              <section className="documentation-nav-group" key={category.id}>
                <h2>{category.label}</h2>
                <ul>
                  {topLevelArticles.map(article => (
                    <li key={article.slug}>
                      <Link
                        aria-current={activeSlug === article.slug ? 'page' : undefined}
                        to={articlePath(article.slug)}>
                        {article.title}
                      </Link>
                    </li>
                  ))}
                </ul>
                {subgroups.map(subgroup => (
                  <details
                    className="documentation-nav-subgroup"
                    key={subgroup.id}
                    open={subgroup.slugs.includes(activeSlug)}>
                    <summary>{subgroup.label}</summary>
                    <ul>
                      {subgroup.slugs.map(slug => getDocumentationArticle(slug)).filter(Boolean).map(article => (
                        <li key={article.slug}>
                          <Link
                            aria-current={activeSlug === article.slug ? 'page' : undefined}
                            to={articlePath(article.slug)}>
                            {article.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}
              </section>
            );
          })}
        </nav>
      </div>
      </details>
    </aside>
  );
};

const DocumentationLayout = ({ activeSlug, children, mainRef, wide = false }) => (
  <div className="documentation-screen d-flex flex-column flex-fill">
    <Header title="Documentation" icon="keyword" />
    <div className="documentation-shell">
      <DocumentationNavigation activeSlug={activeSlug} />
      <main className="documentation-main" ref={mainRef}>
        <div className={`documentation-content${wide ? ' documentation-content--wide' : ''}`}>{children}</div>
      </main>
    </div>
  </div>
);

const CategoryBadge = ({ article }) => (
  <div className="documentation-badges">
    <span className="documentation-badge documentation-category">{categoryLabel(article.category)}</span>
  </div>
);

const ArticleCard = ({ article }) => (
  <article className="documentation-card">
    <CategoryBadge article={article} />
    <h3><Link to={articlePath(article.slug)}>{article.title}</Link></h3>
    <p>{article.summary}</p>
  </article>
);

const DocumentationLibrary = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const articles = query || category !== 'all'
    ? filterDocumentationArticles({ query, category })
    : documentationArticles;
  const groupedArticles = documentationCategories
    .map(group => ({ ...group, articles: articles.filter(article => article.category === group.id) }))
    .filter(group => group.articles.length > 0);
  const resetFilters = () => {
    setQuery('');
    setCategory('all');
  };

  return (
    <DocumentationLayout>
      <header className="documentation-intro">
        <p className="documentation-kicker">AdMobilize guides</p>
        <h2>Build, operate, and understand your deployment</h2>
        <p>Find Dashboard workflows, device setup instructions, integration guides, and API references.</p>
      </header>

      <nav className="documentation-start" aria-label="Start here">
        <span>Start here</span>
        <Link to={articlePath('introduction')}>Basics</Link>
        <Link to={articlePath('workspace-management')}>Workspace management</Link>
        <Link to={articlePath('sensor-network-setup')}>AI sensor network setup</Link>
        <Link to={articlePath('public-api-getting-started')}>API</Link>
      </nav>

      <div className="documentation-filters">
        <label>
          <span>Search documentation</span>
          <input
            aria-label="Search documentation"
            onInput={event => setQuery(event.target.value)}
            placeholder="Search guides"
            type="search"
            value={query}
          />
        </label>
        <div className="documentation-category-filters" role="group" aria-label="Filter by category">
          {[{ id: 'all', label: 'All' }, ...documentationCategories].map(option => (
            <button
              aria-pressed={category === option.id}
              key={option.id}
              onClick={() => setCategory(option.id)}
              type="button">
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {groupedArticles.length ? (
        groupedArticles.map(group => (
          <section className="documentation-category-section" key={group.id}>
            <div className="documentation-category-heading">
              <h2>{group.label}</h2>
              <span>{group.articles.length} {group.articles.length === 1 ? 'guide' : 'guides'}</span>
            </div>
            <div className="documentation-grid">
              {group.articles.map(article => <ArticleCard article={article} key={article.slug} />)}
            </div>
          </section>
        ))
      ) : (
        <div className="documentation-empty">
          <p>No documentation matches those filters.</p>
          <button onClick={resetFilters} type="button">Reset filters</button>
        </div>
      )}
    </DocumentationLayout>
  );
};

const Figures = ({ figures = [] }) => figures.length > 0 && (
  <div className="documentation-figures">
    {figures.map(figure => (
      <figure className="documentation-figure" key={figure.src} style={figure.displayWidth ? { maxWidth: figure.displayWidth } : undefined}>
        <div
          className={`documentation-image${figure.frame ? ' documentation-image--framed' : ''}`}
          style={figure.frame ? { paddingBottom: `${100 * figure.frame.height / figure.frame.width}%` } : undefined}
        >
          <img
            alt={figure.alt}
            loading="lazy"
            src={figureSrc(figure.src)}
            style={figure.frame ? { width: `${100 * figure.frame.sourceWidth / figure.frame.width}%` } : undefined}
          />
        </div>
        <figcaption>
          {figure.caption}{' '}
          <a href={figureSrc(figure.src)} target="_blank" rel="noopener noreferrer">
            Open full size
          </a>
        </figcaption>
      </figure>
    ))}
  </div>
);

const Tables = ({ tables = [], copyUrls = false, copyIdPrefix = 'reference' }) => tables.map((table, tableIndex) => (
  <div
    aria-label={`${table.caption} table`}
    className="documentation-table-wrapper"
    key={table.caption}
    role="region"
    tabIndex={0}>
    <table className={[
      'documentation-table',
      table.headers.length === 2 && 'documentation-table--two-column',
      table.headers.length === 3 && 'documentation-table--three-column',
      table.headers[0] === 'Method' && table.headers[1] === 'Path' && 'documentation-table--routes'
    ].filter(Boolean).join(' ')}>
      <caption>{table.caption}</caption>
      <thead>
        <tr>
          {table.headers.map(header => <th key={header} scope="col">{header}</th>)}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, rowIndex) => (
          <tr key={row.join('|')}>
            {row.map((cell, cellIndex) => {
              const url = copyUrls && cell.match(/https:\/\/[^\s`]+/)?.[0];
              return (
                <td key={table.headers[cellIndex]}>
                  {url ? (
                    <div className="documentation-copyable-value">
                      <span>{renderInlineCode(cell)}</span>
                      <CopyButton
                        id={`${copyIdPrefix}-${tableIndex}-${rowIndex}-${cellIndex}`}
                        text={url}
                        tooltip="Copy URL"
                      />
                    </div>
                  ) : renderInlineCode(cell)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
));

const ArticleParagraph = ({ links = [], paragraph }) => {
  const paragraphLinks = links
    .map(link => ({ ...link, index: paragraph.indexOf(link.label) }))
    .filter(link => link.index >= 0)
    .sort((first, second) => first.index - second.index);
  if (!paragraphLinks.length) return <p>{renderInlineCode(paragraph)}</p>;

  let cursor = 0;
  return (
    <p>
      {paragraphLinks.flatMap(link => {
        const beforeStart = cursor;
        const before = paragraph.slice(cursor, link.index);
        cursor = link.index + link.label.length;
        const linkedLabel = link.slug
          ? (
            <Link
              key={`${link.slug}-${link.index}`}
              to={{
                pathname: articlePath(link.slug),
                hash: link.section ? `#${link.section}` : ''
              }}>
              {link.label}
            </Link>
          )
          : <a href={link.href} key={`${link.href}-${link.index}`}>{link.label}</a>;
        return [...renderInlineCode(before, `text-${beforeStart}`), linkedLabel];
      })}
      {renderInlineCode(paragraph.slice(cursor), `tail-${cursor}`)}
    </p>
  );
};

const SectionProse = ({ section }) => (
  <>
    {(section.paragraphs || []).map(paragraph => (
      <ArticleParagraph
        key={paragraph}
        links={[
          ...(section.paragraphLinks || []),
          ...(section.paragraphLink ? [section.paragraphLink] : [])
        ]}
        paragraph={paragraph}
      />
    ))}
    {section.bullets && (
      <ul className="documentation-list">
        {section.bullets.map(item => <li key={item}>{item}</li>)}
      </ul>
    )}
    {section.steps && (
      <ol>{section.steps.map(step => <li key={step}>{step}</li>)}</ol>
    )}
  </>
);

const highlightMatches = (code, pattern, tokenType) => {
  const parts = [];
  let cursor = 0;

  for (const match of code.matchAll(pattern)) {
    const token = match[0];
    parts.push(code.slice(cursor, match.index));
    parts.push(
      <span className={`documentation-code-token--${tokenType(token, code.slice(match.index + token.length))}`} key={match.index}>
        {token}
      </span>
    );
    cursor = match.index + token.length;
  }

  parts.push(code.slice(cursor));
  return parts;
};

const highlightCode = code => {
  const trimmedCode = code.trimStart();

  if (trimmedCode.startsWith('curl ')) {
    return highlightMatches(
      code,
      /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\bcurl(?=\s)|-[A-Za-z]+|\$[A-Za-z_]\w*/g,
      token => {
        if (token === 'curl') return 'command';
        if (token.startsWith('-')) return 'option';
        if (token.startsWith('$')) return 'variable';
        return /^['"]https?:\/\//.test(token) ? 'url' : 'string';
      }
    );
  }

  if (trimmedCode.startsWith('{') || trimmedCode.startsWith('[')) {
    return highlightMatches(
      code,
      /"(?:\\.|[^"\\])*"|-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?|\b(?:true|false|null)\b/g,
      (token, remaining) => {
        if (token.startsWith('"')) return /^\s*:/.test(remaining) ? 'key' : 'string';
        return /^(?:true|false|null)$/.test(token) ? 'literal' : 'number';
      }
    );
  }

  return code;
};

const CopyableCode = ({ code, id, title }) => {
  const isCommand = code.trimStart().startsWith('curl ');
  const codeTitle = title || (isCommand ? 'cURL' : 'Example');
  const copyTitle = title ? `Copy ${title.toLowerCase()}` : isCommand ? 'Copy command' : 'Copy example';
  return (
    <div className="documentation-code-example">
      <div className="documentation-code-toolbar">
        <span>{codeTitle}</span>
        <CopyButton
          id={`${id}-code`}
          label="Copy"
          text={code}
          tooltip={copyTitle}
        />
      </div>
      <pre><code>{highlightCode(code)}</code></pre>
    </div>
  );
};

const SectionHeading = ({ id, slug, title }) => (
  <div className="documentation-section-heading">
    <h2 id={id} tabIndex={-1}>{title}</h2>
    <CopyButton
      id={`section-link-${slug}-${id}`}
      text={sectionUrl(slug, id)}
      tooltip={`Copy link to ${title}`}
      iconSrc={icons.link}
    />
  </div>
);

const ArticleOutline = ({ activeSectionId, sections, slug }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isWide, setIsWide] = useState(() => window.innerWidth >= 1440);
  const activeTitle = sections.find(section => section.id === activeSectionId)?.title || sections[0]?.title;

  useLayoutEffect(() => {
    const updateWidth = () => {
      setIsWide(window.innerWidth >= 1440);
      setIsOpen(false);
    };
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  return (
    <nav
      aria-label="Table of contents"
      className={`documentation-table-of-contents${isWide ? ' documentation-table-of-contents--side' : ''}${isOpen && !isWide ? ' documentation-table-of-contents--open' : ''}`}>
      {!isWide && (
        <button
          aria-controls={`documentation-outline-${slug}`}
          aria-expanded={isOpen}
          aria-label={`On this page: ${activeTitle}`}
          className="documentation-outline-toggle"
          onClick={() => setIsOpen(open => !open)}
          title={`On this page: ${activeTitle}`}
          type="button">
          On this page
        </button>
      )}
      <div className="documentation-outline-panel" hidden={!isWide && !isOpen} id={`documentation-outline-${slug}`}>
        <h2>On this page</h2>
        <ul>
          {sections.map(section => (
            <li key={section.id}>
              <Link
                aria-current={activeSectionId === section.id ? 'location' : undefined}
                onClick={event => {
                  if (jumpToSection(event, section.id) && !isWide) setIsOpen(false);
                }}
                to={{ pathname: articlePath(slug), hash: `#${section.id}` }}>
                {section.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

const DocumentationArticle = ({ activeSectionId, article, dashboardLinks, mainRef, showBackToTop }) => {
  const nextArticle = article.nextArticle && getDocumentationArticle(article.nextArticle.slug);
  const isDeveloperArticle = article.category === 'developer';
  const outlineSections = [
    ...article.sections,
    ...(dashboardLinks.length > 0 ? [{ id: 'dashboard-links', title: 'Dashboard links' }] : []),
    ...(article.related.length > 0 ? [{ id: 'related-articles', title: 'Related articles' }] : []),
    ...(nextArticle ? [{ id: 'continue-learning', title: 'Continue learning' }] : [])
  ];

  return (
    <DocumentationLayout activeSlug={article.slug} mainRef={mainRef} wide={isDeveloperArticle}>
      <div className="documentation-article-frame">
        <article className={`documentation-article${isDeveloperArticle ? ' documentation-article--developer' : ''}`}>
        <nav className="documentation-breadcrumb" aria-label="Breadcrumb">
          <Link to="/documentation">Documentation</Link>
          <span aria-hidden="true">/</span>
          <span>{article.title}</span>
        </nav>

        <CategoryBadge article={article} />
        <h1 tabIndex={-1}>{article.title}</h1>
        <p className="documentation-summary">{article.summary}</p>

        {article.legacyNotice && (
          <aside className="documentation-legacy">
            <strong>{article.legacyNotice.title}</strong>
            {article.legacyNotice.paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
            <p>
              {article.legacyNotice.replacement.before}
              <Link to={articlePath(article.legacyNotice.replacement.slug)}>
                {article.legacyNotice.replacement.label}
              </Link>
              {article.legacyNotice.replacement.after}
            </p>
          </aside>
        )}

        {article.sections.map(section => {
          const hasReference = section.code || section.response || section.tables?.length || section.figures?.length;
          const copyIdPrefix = `${article.slug}-${section.id}`;
          return (
            <section className="documentation-section" key={section.id}>
              <SectionHeading id={section.id} slug={article.slug} title={section.title} />
              {isDeveloperArticle && hasReference ? (
                <div className="documentation-api-section-layout">
                  <div className="documentation-api-section-prose"><SectionProse section={section} /></div>
                  <div className="documentation-api-section-reference">
                    <Tables tables={section.tables} copyUrls copyIdPrefix={copyIdPrefix} />
                    {section.code && <CopyableCode code={section.code} id={copyIdPrefix} title={section.response ? 'Request' : undefined} />}
                    {section.response && <CopyableCode code={section.response} id={`${copyIdPrefix}-response`} title="Response" />}
                    <Figures figures={section.figures} />
                  </div>
                </div>
              ) : (
                <>
                  <SectionProse section={section} />
                  {section.code && <pre><code>{section.code}</code></pre>}
                  {section.response && <pre><code>{section.response}</code></pre>}
                  <Tables tables={section.tables} />
                  <Figures figures={section.figures} />
                </>
              )}
            </section>
          );
        })}

        {dashboardLinks.length > 0 && (
          <section className="documentation-section documentation-link-section">
            <SectionHeading id="dashboard-links" slug={article.slug} title="Dashboard links" />
            <ul>
              {dashboardLinks.map(link => (
                <li key={link.to}><Link to={link.to}>{link.label}</Link></li>
              ))}
            </ul>
          </section>
        )}

        {article.related.length > 0 && (
          <section className="documentation-section documentation-link-section">
            <SectionHeading id="related-articles" slug={article.slug} title="Related articles" />
            <ul>
              {article.related.map(slug => {
                const related = getDocumentationArticle(slug);
                return related && <li key={slug}><Link to={articlePath(slug)}>{related.title}</Link></li>;
              })}
            </ul>
          </section>
        )}

        {nextArticle && (
          <section className="documentation-section documentation-link-section documentation-next-article">
            <SectionHeading id="continue-learning" slug={article.slug} title="Continue learning" />
            <p>{article.nextArticle.description}</p>
            <p>
              <Link to={articlePath(nextArticle.slug)}>Continue to {nextArticle.title} →</Link>
            </p>
          </section>
        )}

        {showBackToTop && (
          <button
            className="documentation-back-to-top"
            onClick={returnToArticleTop}
            type="button">
            Back to top <span aria-hidden="true">↑</span>
          </button>
        )}
        </article>
        <ArticleOutline activeSectionId={activeSectionId} sections={outlineSections} slug={article.slug} />
      </div>
    </DocumentationLayout>
  );
};

const DocumentationScreen = ({
  isBrandUser = false,
  isWhitelabel,
  location = {},
  match = { params: {} }
}) => {
  const slug = match.params && match.params.slug;
  const sectionId = location.hash && location.hash.slice(1);
  const article = slug && getDocumentationArticle(slug);
  const mainRef = useRef(null);
  const [activeSectionId, setActiveSectionId] = useState(sectionId || '');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useLayoutEffect(() => {
    const scrollContainer = mainRef.current;
    if (!slug || !scrollContainer) return undefined;

    const storageKey = articleScrollKey(slug);
    const sectionHeading = sectionId && document.getElementById(sectionId);
    if (sectionHeading) {
      setActiveSectionId(sectionId);
      sectionHeading.focus({ preventScroll: true });
      sectionHeading.scrollIntoView({ block: 'start' });
    } else {
      setActiveSectionId(article?.sections[0]?.id || '');
      const savedPosition = Number(window.sessionStorage.getItem(storageKey));
      scrollContainer.scrollTop = sectionId
        ? 0
        : (Number.isFinite(savedPosition) ? savedPosition : 0);
    }

    return () => window.sessionStorage.setItem(storageKey, String(scrollContainer.scrollTop));
  }, [sectionId, slug]);

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!slug || !main) return undefined;

    const shell = main.closest('.documentation-shell');
    const article = main.querySelector('.documentation-article');
    const sectionHeadings = [...main.querySelectorAll('.documentation-section-heading h2[id]')];
    const updateVisibility = () => {
      if (!shell || !article) return;
      const isMobile = window.innerWidth <= 992;
      setShowBackToTop(isMobile
        ? shell.scrollTop > 0 && article.getBoundingClientRect().top < shell.getBoundingClientRect().top
        : main.scrollTop > 0);

      const headingPositions = sectionHeadings.map(heading => heading.getBoundingClientRect().top);
      if (!headingPositions.length || headingPositions.every(position => position === 0)) return;
      const viewportTop = (isMobile ? shell : main).getBoundingClientRect().top + 120;
      let currentSection = sectionHeadings[0].id;
      for (let index = 0; index < sectionHeadings.length; index++) {
        if (headingPositions[index] > viewportTop) break;
        currentSection = sectionHeadings[index].id;
      }
      setActiveSectionId(currentSection);
    };

    main.addEventListener('scroll', updateVisibility);
    shell?.addEventListener('scroll', updateVisibility);
    window.addEventListener('resize', updateVisibility);
    updateVisibility();

    return () => {
      main.removeEventListener('scroll', updateVisibility);
      shell?.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [slug]);

  if (!slug) return <DocumentationLibrary />;

  if (!article) return <NotFoundScreen isWhitelabel={isWhitelabel} />;

  const dashboardLinks = article.dashboardLinks.filter(link =>
    !isBrandUser || ['/charts', '/profile'].includes(link.to));
  return <DocumentationArticle
    activeSectionId={activeSectionId}
    article={article}
    dashboardLinks={dashboardLinks}
    mainRef={mainRef}
    showBackToTop={showBackToTop}
  />;
};

export default DocumentationScreen;
